import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination, TableSortLabel,
  TextField, InputAdornment, IconButton, Chip, CircularProgress,
  Menu, MenuItem, Button, Dialog, DialogTitle, DialogContent, Popover,
  Drawer, Divider, Tooltip
} from '@mui/material';
import { AiOutlineSearch } from 'react-icons/ai';
import { MdKeyboardArrowRight, MdFilterList, MdClose, MdContentCopy, MdFileDownload } from 'react-icons/md';
import { PiFileCsv } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';
import { fetchAllInvoices } from '../../api/dashboardApi';
import { useVendor } from '../../utils/Context';
import InvoiceDetailView from './InvoiceDetailView';

const formatCurrency = (val, prefix = '') => {
  if (val === null || val === undefined || val === '') return '—';
  const num = Number(String(val).replace(/,/g, ''));
  if (isNaN(num)) return val;
  return `${prefix}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val) => {
  if (!val) return '—';
  const date = new Date(val);
  if (isNaN(date.getTime())) return val;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const InvoiceRow = ({ row, isExpanded, onToggle, onOpenPdf }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenDialog = (event) => {
    event.stopPropagation();
    handleMenuClose();
    onOpenPdf(row.enc_name);
  };

  const handleOpenWindow = (event) => {
    event.stopPropagation();
    handleMenuClose();
    window.open(row.enc_name, '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  return (
    <TableRow
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f8fafc' },
        transition: 'all 0.2s ease',
        backgroundColor: isExpanded ? '#f1f5f9' : 'transparent',
      }}
    >
      <TableCell sx={{ width: '15px', py: 1, px: 1, whiteSpace: 'nowrap' }}>
        <IconButton size="small" sx={{ p: 0.3 }}>
          <MdKeyboardArrowRight size={18} />
        </IconButton>
      </TableCell>
        <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: '#7C2EA0', py: 1, whiteSpace: 'nowrap' }}>
          {row.invoice_id}
          <Tooltip title="Copy Invoice ID" arrow placement="top">
            <IconButton
              size="small"
              sx={{ ml: 0.5, p: 0.2 }}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(row.invoice_id);
              }}
            >
              <MdContentCopy size={12} color="#94a3b8" />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, whiteSpace:"nowrap" }}>{formatDate(row.invoice_date)}</TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, whiteSpace: 'nowrap' }}>
          <Chip label={row.currency || '—'} size="small" sx={{ fontSize: '10px', height: 20, fontWeight: 600 }} />
        </TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.currency ? `${row.currency} ` : ''}{formatCurrency(row.total_amount)}</TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, whiteSpace: 'nowrap' }}>
          <Tooltip title={String(row.conversion_rate ?? '—')} arrow placement="top">
            <span style={{ cursor: 'help' }}>{row.conversion_rate != null ? Number(row.conversion_rate).toFixed(3) : '—'}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: 600, whiteSpace: 'nowrap' }}>
          <Tooltip 
            title={`${row.currency ? `${row.currency} ` : ''}${formatCurrency(row.total_amount)} × ${row.conversion_rate != null ? Number(row.conversion_rate).toFixed(3) : 1} = USD ${formatCurrency(row.total_usd)}`} 
            arrow 
            placement="top"
          >
            <span style={{ cursor: 'help', borderBottom: '1px dashed #cbd5e1' }}>
              USD {formatCurrency(row.total_usd)}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: 600, color: Number(row.grouped_discrepancy_amount) > 0 ? '#dc2626' : 'inherit', whiteSpace: 'nowrap' }}>USD {formatCurrency(row.grouped_discrepancy_amount)}</TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>USD {formatCurrency(row.total_adj)}</TableCell>
        <TableCell sx={{ fontSize: '12px', py: 1, fontWeight: 600, color: Number(row.grouped_discrepancy_final_amount) > 0 ? '#dc2626' : 'inherit', whiteSpace: 'nowrap' }}>USD {formatCurrency(row.grouped_discrepancy_final_amount)}</TableCell>


        <TableCell sx={{ py: 1, whiteSpace:"nowrap" }}>
          {row.enc_name && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={handleMenuClick}
                sx={{ textTransform: 'none', color: '#0ea5e9', fontSize: '12px', fontWeight: 600, minWidth: 0, p: 0 }}
              >
                View
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem onClick={handleOpenDialog} sx={{ fontSize: '13px' }}>Open in Dialog</MenuItem>
                <MenuItem onClick={handleOpenWindow} sx={{ fontSize: '13px' }}>Open in New Window</MenuItem>
              </Menu>
            </>
          )}
        </TableCell>
    </TableRow>
  );
};

const headCells = [
  { id: 'expand', label: '', sortable: false, width: '20px' },
  { id: 'invoice_id', label: 'Inv No', tooltip: 'Invoice Number', sortable: true },
  { id: 'invoice_date', label: 'Date', sortable: true, width: '70px' },
  { id: 'currency', label: 'Curr', tooltip: 'Currency', sortable: true, width: '40px' },
  { id: 'total_amount', label: 'Total Amt', tooltip: 'Total Amount', sortable: true },
  { id: 'conversion_rate', label: 'Conv. Rate', tooltip: 'Conversion Rate', sortable: true, width: '60px' },
  { id: 'total_usd', label: 'Total Amt(USD)', tooltip: 'Total Amount (USD)', sortable: true },
  { id: 'grouped_discrepancy_amount', label: 'Discrep.', tooltip: 'Discrepancy', sortable: true },
  { id: 'total_adj', label: 'Settled', tooltip: 'Settled Amount', sortable: true },
  { id: 'grouped_discrepancy_final_amount', label: 'Final', tooltip: 'Final Amount', sortable: true },
  { id: 'view_url', label: 'URL', tooltip: 'Invoice URL', sortable: false, width: '70px' },
];

const InvoiceListing = () => {
  const { selectedVendor } = useVendor();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('invoice_date');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterOpen = Boolean(filterAnchorEl);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleApplyFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setPage(0);
    handleFilterClose();
  };
  
  const handleClearFilter = () => {
    setTempStartDate('');
    setTempEndDate('');
    setStartDate('');
    setEndDate('');
    setPage(0);
    handleFilterClose();
  };

  const handleOpenPdfDialog = (url) => {
    setCurrentPdfUrl(url);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setCurrentPdfUrl('');
  };

  const { data: response, isLoading } = useQuery({
    queryKey: ['allInvoices', selectedVendor],
    queryFn: () => fetchAllInvoices({ vendor_id: selectedVendor }),
    staleTime: 5 * 60 * 1000,
  });

  const invoices = useMemo(() => {
    if (!response?.data) return [];
    return response.data.map((el) => ({
      ...el,
      status: el?.status === 'processed' ? 'Processed' : 'Pending',
      discrepancy_detected: el.discrepancy_detected ? 'True' : 'False',
      invoice_date: el?.invoice_date ? new Date(el.invoice_date) : null,
      final_discrepancy: (Number(el.total_discrepancy) || 0) - (Number(el.total_adj) || 0),
    }));
  }, [response]);

  const filteredData = useMemo(() => {
    let result = invoices;

    if (startDate) {
      const startMs = new Date(startDate).getTime();
      result = result.filter(inv => inv.invoice_date && inv.invoice_date.getTime() >= startMs);
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);
      const endMs = end.getTime();
      result = result.filter(inv => inv.invoice_date && inv.invoice_date.getTime() < endMs);
    }

    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase();
    
    return result.filter((inv) => {
      return Object.values(inv).some((val) => {
        if (val === null || val === undefined) return false;
        
        if (val instanceof Date) {
          const formatted = formatDate(val).toLowerCase();
          if (formatted.includes(q)) return true;
        }
        
        return String(val).toLowerCase().includes(q);
      });
    });
  }, [invoices, searchQuery, startDate, endDate]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric string comparison
      if (['total_amount', 'total_usd', 'total_discrepancy', 'final_discrepancy', 'grouped_discrepancy_amount', 'total_adj', 'grouped_discrepancy_final_amount', 'conversion_rate'].includes(orderBy)) {
        aVal = Number(String(aVal || '').replace(/,/g, '')) || 0;
        bVal = Number(String(bVal || '').replace(/,/g, '')) || 0;
      }

      if (aVal instanceof Date && bVal instanceof Date) {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy]);

  const handleExportMainCSV = () => {
    if (filteredData.length === 0) return;

    const headers = ['Invoice No', 'Date', 'Currency', 'Total Amt', 'Conv. Rate', 'Total Amt(USD)', 'Discrepancy', 'Settled', 'Final'];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let csvContent = headers.join(',') + '\n';
    
    filteredData.forEach(row => {
      const locCur = row.currency ? `${row.currency} ` : '';
      const rowData = [
        row.invoice_id,
        formatDate(row.invoice_date),
        row.currency,
        formatCurrency(row.total_amount, locCur),
        row.conversion_rate,
        formatCurrency(row.total_usd, 'USD '),
        formatCurrency(row.grouped_discrepancy_amount, 'USD '),
        formatCurrency(row.total_adj, 'USD '),
        formatCurrency(row.grouped_discrepancy_final_amount, 'USD ')
      ];
      csvContent += rowData.map(escapeCSV).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Invoice_Records.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const expandedInvoice = useMemo(() => {
    if (!expandedId) return null;
    return sortedData.find(row => (row.invoice_id || row.id) === expandedId);
  }, [expandedId, sortedData]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Paper sx={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#1e293b">Invoice Records</Typography>
          <Typography variant="caption" color="#94a3b8">
            {filteredData.length} invoice{filteredData.length !== 1 ? 's' : ''} found • Click a row to expand details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Download CSV" placement="top" arrow>
            <span
              onClick={handleExportMainCSV}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                backgroundColor: "#f5f5f5",
                cursor: filteredData.length > 0 ? "pointer" : "default",
                opacity: filteredData.length > 0 ? 1 : 0.5,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (filteredData.length > 0) {
                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
            >
              <PiFileCsv size={20} color="#333" />
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={handleFilterClick}
            startIcon={<MdFilterList />}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#475569', borderColor: '#cbd5e1', height: '36px' }}
          >
            Filter Dates {(startDate || endDate) && <Chip label="!" size="small" sx={{ ml: 1, height: 16, fontSize: '10px', bgcolor: '#7C2EA0', color: '#fff' }} />}
          </Button>
          <Popover
            open={filterOpen}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            transitionDuration={300}
            PaperProps={{ sx: { p: 2, mt: 1, borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Filter by Date Range</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type="date"
                  size="small"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  sx={{ width: 140 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  sx={{ width: 140 }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button size="small" onClick={handleClearFilter} sx={{ color: '#64748b', textTransform: 'none' }}>Clear</Button>
                <Button size="small" variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#7C2EA0', '&:hover': { bgcolor: '#5f237a' }, textTransform: 'none' }}>Apply Filter</Button>
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search invoices..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', height: '36px' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><AiOutlineSearch size={16} color="#94a3b8" /></InputAdornment>,
            }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: '#7C2EA0' }} />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        fontWeight: 700, fontSize: '11px', backgroundColor: '#f8fafc',
                        color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                        py: 1, whiteSpace: 'nowrap', width: cell.width || 'auto'
                      }}
                      sortDirection={orderBy === cell.id ? order : false}
                    >
                      {cell.sortable ? (
                        <Tooltip title={cell.tooltip || cell.label} arrow placement="top">
                          <TableSortLabel
                            active={orderBy === cell.id}
                            direction={orderBy === cell.id ? order : 'asc'}
                            onClick={() => handleSort(cell.id)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        </Tooltip>
                      ) : (
                        <Tooltip title={cell.tooltip || cell.label} arrow placement="top">
                          <span>{cell.label}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <InvoiceRow
                      key={row.invoice_id || row.id}
                      row={row}
                      isExpanded={expandedId === (row.invoice_id || row.id)}
                      onToggle={() => setExpandedId(expandedId === (row.invoice_id || row.id) ? null : (row.invoice_id || row.id))}
                      onOpenPdf={handleOpenPdfDialog}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                      No invoices found matching your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: '1px solid #e5e7eb' }}
          />
        </>
      )}
      <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" fontSize="16px" fontWeight="bold">Invoice Preview</Typography>
          <Button onClick={handleClosePdfDialog} color="inherit" size="small">Close</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {currentPdfUrl && (
            <iframe
              src={currentPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Drawer
        anchor="right"
        open={Boolean(expandedId)}
        onClose={() => setExpandedId(null)}
        PaperProps={{ sx: { width: '80%', maxWidth: '1000px', backgroundColor: '#f8fafc' } }}
      >
        {expandedInvoice && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight="bold">
                Invoice Details: {expandedInvoice.invoice_id}
              </Typography>
              <IconButton onClick={() => setExpandedId(null)}>
                <MdClose />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              <InvoiceDetailView 
                invoice={{
                  ...expandedInvoice,
                  total_amount_raw: String(expandedInvoice.total_amount || '').replace(/,/g, ''),
                  total_usd_raw: String(expandedInvoice.total_usd || '').replace(/,/g, ''),
                }}
                onOpenPdf={handleOpenPdfDialog}
              />
            </Box>
          </Box>
        )}
      </Drawer>
    </Paper>
  );
};

export default InvoiceListing;
